import React, { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { ALL_STAGES, STAGE_COLORS, formatCurrency, daysBetween } from "./utils";

/**
 * CompletedItemsSection — collapsible Dashboard footer showing every
 * Completed line. Clients filter narrows the list when ≥2 clients are
 * present. Each row expands into its stage-time breakdown.
 */
export default function CompletedItemsSection({ items, isAdmin, showValue }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filterClient, setFilterClient] = useState("all");
  const completedItems = items.filter(i => i.stage === "Completed");
  const uniqueClients = [...new Set(completedItems.map(i => i.client))].filter(Boolean);
  const filteredCompleted = filterClient === "all" ? completedItems : completedItems.filter(i => i.client === filterClient);

  if (completedItems.length === 0) return null;

  return (
    <div data-testid="completed-items-section">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setIsExpanded(e => !e)}
          className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 rounded"
          aria-expanded={isExpanded}
          data-testid="completed-section-toggle"
        >
          {isExpanded ? <ChevronDown className="w-4 h-4 text-[hsl(var(--muted-foreground))]" /> : <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />}
          <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))] flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-zinc-400" /> Completed Items
          </h2>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-zinc-500/10 text-zinc-500 border border-zinc-500/20" data-testid="completed-count-badge">{completedItems.length}</span>
        </button>
        {isExpanded && uniqueClients.length > 1 && (
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="h-8 w-[140px] text-xs bg-[hsl(var(--muted))] border-[hsl(var(--border))]" data-testid="completed-client-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {uniqueClients.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {isExpanded && (
        <div className="space-y-1">
          {filteredCompleted.map(item => (
            <CompletedItemRow key={item.id} item={item} isAdmin={isAdmin} showValue={showValue} />
          ))}
        </div>
      )}
    </div>
  );
}

function CompletedItemRow({ item, isAdmin, showValue }) {
  const [isOpen, setIsOpen] = useState(false);
  const totalDays = daysBetween(item.po_date, item.due_date);
  const hasRealData = totalDays > 0;

  // Approximate stage distribution from total cycle. Replace with real
  // stage_ages data once persisted across the pipeline.
  const stageDistribution = hasRealData
    ? {
        "PO Received":     Math.max(1, Math.round(totalDays * 0.10)),
        "Procurement":     Math.max(1, Math.round(totalDays * 0.25)),
        "Manufacturing":   Math.max(1, Math.round(totalDays * 0.35)),
        "Quality Control": Math.max(1, Math.round(totalDays * 0.15)),
        "Dispatch":        Math.max(1, Math.round(totalDays * 0.15)),
      }
    : null;

  const totalProcSpend = (item.material_spend || 0) + (item.tooling_spend || 0) + (item.gauging_spend || 0);
  const grossMarginPct = isAdmin && showValue && item.value_usd > 0 && totalProcSpend > 0
    ? ((item.value_usd - totalProcSpend) / item.value_usd * 100).toFixed(1)
    : null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full" data-testid={`completed-item-${item.id}`}>
        <div className="flex items-center justify-between p-2.5 border border-[hsl(var(--border))] rounded-lg hover:bg-[hsl(var(--muted))]/30 transition-colors cursor-pointer text-sm">
          <div className="flex items-center gap-2.5">
            {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <span className="font-medium text-[12px] leading-snug break-words max-w-[280px]">{item.item_description}</span>
            <span className="font-mono-data text-xs font-semibold px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">PO</span>
              {item.po_number}
            </span>
            {item.is_qual && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 border border-orange-500/30">QUAL</span>}
          </div>
          <div className="flex items-center gap-3">
            {grossMarginPct !== null && (
              <span className={`text-xs font-mono-data font-bold px-2 py-0.5 rounded-full ${parseFloat(grossMarginPct) >= 70 ? 'bg-green-500/10 text-green-600' : parseFloat(grossMarginPct) >= 40 ? 'bg-amber-500/10 text-amber-600' : 'bg-red-500/10 text-red-500'}`} data-testid={`gross-margin-${item.id}`}>
                GM {grossMarginPct}%
              </span>
            )}
            {showValue && <span className="font-mono-data text-xs text-[hsl(var(--muted-foreground))]">{formatCurrency(item.value_usd)}</span>}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-6 mt-1 mb-2 flex items-center gap-1 flex-wrap" data-testid={`stage-timeline-${item.id}`}>
          {ALL_STAGES.filter(s => s !== "Completed").map((stage, idx) => {
            const sc = STAGE_COLORS[stage];
            const days = stageDistribution ? stageDistribution[stage] : null;
            return (
              <React.Fragment key={stage}>
                {idx > 0 && <span className="text-[hsl(var(--muted-foreground))] text-xs mx-0.5">&rarr;</span>}
                <span className="text-xs font-medium px-2 py-1 rounded" style={{ color: sc.color, backgroundColor: sc.bg }}>
                  {stage.replace("Quality Control", "QC")}: <span className="font-mono-data font-bold">{days ? `~${days}d` : "n/a"}</span>
                </span>
              </React.Fragment>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
